import { EventType } from "@/analytics/analytics-events";
import { SelectWaypointsColorizationProperties } from "@/analytics/viewer-settings-events-properties";
import { useSettingsContext } from "@/components/common/settings-context/settings-context";
import { WaypointsColoringOptions } from "@/store/view-options/view-options-slice";
import { Dropdown, FaroText, Option } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { validateEnumValue } from "@faro-lotv/foundation";
import { Stack } from "@mui/material";
import { useMemo, type JSX } from "react";
import { HelpPopover } from "../help-popover";

type WaypointsColorizationOptionsProps = {
  /** Whether the 'color by elevation' option should be disabled */
  disableColorByElevation: boolean;
  /** Whether the 'color by date' option should be disabled */
  disableColorByDate: boolean;
};

/** @returns An UI element with labels and dropdown providing waypoints colorization options to the user */
export function WaypointsColorizationOptions({
  disableColorByElevation,
  disableColorByDate,
}: WaypointsColorizationOptionsProps): JSX.Element {
  const { waypointsColoring, setWaypointsColoring } = useSettingsContext();

  const dropdownOptions: Option[] = useMemo(
    () => [
      {
        key: WaypointsColoringOptions.custom,
        value: WaypointsColoringOptions.custom,
        label: "Custom",
        isDisabled: false,
      },
      {
        key: WaypointsColoringOptions.byElevation,
        value: WaypointsColoringOptions.byElevation,
        label: "By elevation",
        isDisabled: disableColorByElevation,
      },
      {
        key: WaypointsColoringOptions.byCaptureDate,
        value: WaypointsColoringOptions.byCaptureDate,
        label: "By capture date",
        isDisabled: disableColorByDate,
      },
    ],
    [disableColorByElevation, disableColorByDate],
  );

  return (
    <Stack
      direction="row"
      aria-label="Waypoints colorization setting"
      sx={{ alignItems: "center", justifyContent: "space-between" }}
    >
      <Stack direction="row" sx={{ alignItems: "center" }}>
        <FaroText variant="bodyM" dark>
          Colorize
        </FaroText>
        <HelpPopover
          title="Waypoints color"
          description={
            <>
              Customize the waypoints color based on their:
              <ul>
                <li>Elevation</li>
                <li>Capture date</li>
                <li>Custom color</li>
              </ul>
            </>
          }
        />
      </Stack>
      <Dropdown
        dark
        aria-label="Waypoints colorization dropdown options"
        variant="underlined"
        fullWidth={false}
        shouldCapitalize={false}
        options={dropdownOptions}
        value={waypointsColoring}
        onChange={(e) => {
          const option = e.target.value;
          if (validateEnumValue(option, WaypointsColoringOptions)) {
            Analytics.track<SelectWaypointsColorizationProperties>(
              EventType.selectWaypointsColorization,
              {
                value: option,
              },
            );
            setWaypointsColoring(option, true);
          }
        }}
      />
    </Stack>
  );
}
